import React, { useCallback, useEffect, useState } from 'react';

import { Grid } from '@lp/ds-next';
import { Tabs, TabsProps } from 'antd';
import queryString from 'query-string';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import BoxChecklist from '../components/BoxChecklist';
import BoxConfig from '../components/BoxConfig';
import BoxesSticker from '../components/BoxesSticker';
import BoxFirmwareHistory from '../components/BoxFirmwareHistory';
import BoxHardware from '../components/BoxHardware';
import BoxLastLocationMap from '../components/BoxLastLocationMap';
import { BoxLifecycle } from '../components/BoxLifecycle';
import BoxManagement from '../components/BoxManagement';
import BoxNotes from '../components/BoxNotes/BoxNotes';
import BoxQualityCheck from '../components/BoxQualityCheck';
import BoxTelemetryEvents from '../components/BoxTelemetryEvents';
import GoToBoxShipmentButton from '../components/GoToBoxShipmentButton';
import ProductDetailsForm from '../components/ProductDetailsForm';
import RemoteControl from '../components/RemoteControl/RemoteControl';
import SimVersion from '../components/SimVersions';
import useGetBox from '../hooks/useGetBox';
import { BoxOta } from '../ota';
import PageTitle from '@/components/layout/PageTitle';
import { scopes } from '@/config';
import { IShipment } from '@/features/shipments/models/shipment';
import { useApiClient } from '@/helpers/ApiClients';
import useAppState from '@/hooks/useAppState';

interface IShipmentListResponse {
  items: IShipment[];
}

const ActionButtonBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const ProductDetailView = () => {
  // 1. Generic hooks
  const { id } = useParams<{ id: string }>();
  const shipmentService = useApiClient('shipment');
  const userScopes = useAppState((state) => state.scopes);

  // 2. Domain hooks
  const { box, getBox } = useGetBox(id);

  // 3. Local state
  const [tab, setTab] = useState('telemetry');
  const [tabItems, setTabItems] = useState<TabsProps['items']>([]);
  const [currentShipment, setCurrentShipment] = useState<IShipment>();
  const [nbShipment, setNbShipment] = useState<number>(0);

  // 5. Callbacks
  const findCurrentShipment = useCallback(
    (data: IShipmentListResponse) => {
      // Early return: No shipments
      if (!data.items || data.items.length === 0) {
        setCurrentShipment(undefined);
        setNbShipment(0);

        return;
      }

      // Filter shipments that are physically on the box
      const activeShipments = data.items.filter(
        (s: IShipment) =>
          !s.isArchived &&
          (s.contractState === 'TRANSFERRED' || s.contractState === 'DEPLOYED')
      );

      // Update count
      setNbShipment(activeShipments.length);

      // Early return: No active shipments
      if (activeShipments.length === 0) {
        setCurrentShipment(undefined);

        return;
      }

      // Select most recent by updatedAt
      const mostRecentShipment = activeShipments.reduce(
        (prev: IShipment, current: IShipment) =>
          (current.updatedAt || 0) > (prev.updatedAt || 0) ? current : prev
      );

      setCurrentShipment(mostRecentShipment);
    },
    [setCurrentShipment]
  );

  const refreshBoxShipment = useCallback(
    async (boxId: string): Promise<void> => {
      const params = queryString.stringify({
        boxId,
        lastdeployed: true,
        sortBy: 'deployedAt',
        order: 'asc',
      });

      try {
        const { data } = await shipmentService.get<IShipmentListResponse>(
          `/shipments?${params}`
        );

        findCurrentShipment(data);
      } catch (error) {
        console.error('[refreshBoxShipment] Error fetching shipments:', error);
        setCurrentShipment(undefined);
        setNbShipment(0);
      }
    },
    [shipmentService, findCurrentShipment]
  );

  // Callback to refresh after cleanup
  const handleCleanupComplete = useCallback(() => {
    // refresh box information
    getBox();
    // refresh shipments
    if (box.id) {
      refreshBoxShipment(box.id);
    }
  }, [getBox, box.id, refreshBoxShipment]);

  // 6. Effects
  useEffect(() => {
    if (box.id) {
      refreshBoxShipment(box.id);
      const items: TabsProps['items'] = [
        {
          label: 'Telemetry Events',
          key: 'telemetry',
          children: <BoxTelemetryEvents id={box.id} />,
          forceRender: true,
        },
        {
          label: 'Firmware',
          key: 'firmware',
          children: <BoxFirmwareHistory box={box} />,
        },
        {
          label: 'Hardware',
          key: 'hardware',
          children: <BoxHardware box={box} />,
        },
        {
          label: 'Connectivity',
          key: 'connectivity',
          children: <SimVersion box={box} />,
        },
        {
          label: 'Product Sticker',
          key: 'production-sticker',
          children: <BoxesSticker box={box} />,
        },
        {
          label: 'Box Status',
          key: 'box-status',
          children: <BoxChecklist boxId={box.id!} />,
        },
        {
          label: 'Notes',
          key: 'notes',
          children: <BoxNotes boxId={box.id!} />,
        },
        {
          label: 'Firmware Config',
          key: 'firmware-config',
          children: <BoxConfig boxId={box.id!} />,
        },
        {
          label: 'Box Quality Check',
          key: 'box-quality-check',
          children: <BoxQualityCheck boxId={box.id!} />,
        },
        {
          label: 'Lifecycle',
          key: 'lifecycle',
          children: <BoxLifecycle boxId={box.lpUI} />,
        },
        {
          label: 'Map',
          key: 'map',
          children: <BoxLastLocationMap boxId={box.id} thingName={box.lpUI} />,
        },
        {
          label: 'OTA',
          key: 'ota',
          children: <BoxOta lpUI={box.lpUI} />,
        },
      ];

      const responsibilityHistoryTab = {
        label: 'Responsibility History',
        key: 'box-management',
        children: <BoxManagement id={box.id} />,
      };

      items.splice(4, 0, responsibilityHistoryTab);

      setTabItems(items);
    }
  }, [
    box,
    findCurrentShipment,
    shipmentService,
    userScopes,
    refreshBoxShipment,
  ]);

  return (
    <Grid container justifyContent="center">
      <Grid item mobile={11}>
        <PageTitle title="PRODUCT DETAILS" />
      </Grid>
      <Grid
        item
        mobile={11}
        container
        justifyContent="flex-end"
        style={{ gap: '0.5rem' }}
      >
        {userScopes.includes(scopes.MANAGE_PRODUCT) && id && (
          <RemoteControl
            shipment={currentShipment}
            onCleanupComplete={handleCleanupComplete}
          />
        )}
        <ActionButtonBar>
          <GoToBoxShipmentButton boxId={box.id} shipment={currentShipment} />
        </ActionButtonBar>
      </Grid>
      <Grid item mobile={11}>
        <ProductDetailsForm
          box={box}
          shipment={currentShipment}
          nbShipment={nbShipment}
        />
      </Grid>
      <Grid item mobile={11} style={{ marginTop: '2rem' }}>
        <Tabs
          type="card"
          activeKey={tab}
          onTabClick={(key) => setTab(key)}
          items={tabItems}
          data-testid="product-details-tabs"
        />
      </Grid>
    </Grid>
  );
};
